import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import classNames from 'classnames';
import createCachedSelector from 're-reselect';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { createSelector } from 'reselect';

import { TreeNav } from 'ui-components/components/tree-navigation';
import { noop } from 'ui-components/default-props/noop';

import { messages } from './messages';
import { getSelectedViewFromPathName, ShippingLeftNavContext } from './shipping-left-nav-context';
import style from './shipping-left-nav.module.less';
import AdvancedSearchToggler from '../../../advanced-search/advanced-search-toggler';
import {
  useIsOnReturnsAdvancedSearchView,
  useIsOnBatchesAdvancedSearchView,
  useIsOnShipmentsAdvancedSearchView,
  useIsOnFulfillmentsAdvancedSearchView,
} from '../../../advanced-search/hooks';
import AdvancedSearch from '../../../advanced-search/shipments';
import { SearchForm } from '../../../components/shipping-grid/search-form';
import { shippingGridViews } from '../../../components/shipping-grid/views';
import { ViewFilters } from '../../../components/shipping-grid/views/view-filters';
import {
  SHIPPED_VIEW_ID,
  RETURNS_VIEW_ID,
  ADVANCED_SEARCH_VIEW_ID,
  BATCHES_ADVANCED_SEARCH_VIEW_ID,
  FULFILLMENTS_ADVANCED_SEARCH_VIEW_ID,
  END_OF_DAY_CATEGORY_VIEW_ID,
  CONTAINER_SHIPMENTS_VIEW_ID,
  REFUND_ASSIST_VIEW_ID,
} from '../../../components/shipping-grid/views/view-id-constants';
import { emptyArray } from 'ui-components/default-props/empty';
import { useEnableV3ContainerLabelShipments } from '../../../feature-flags/hooks/use-enable-container-label-shipments';
import { useIsRefundAssistGridVisible } from '../../../hooks/refund-assist/use-is-refund-assist-visible';
import { BASE_SHIPMENTS_PATH, SHIPMENTS_RETURNS_PATH } from '../../../routes/constants';
import { ShipmentsGridViewService } from '../../../services/shipments-grid-view-service';
import { treeMap } from '../../../services/tree-service';
import Global from '../../../store/global-selectors';
import { returnsSearch } from '../../../store/returns/nav/returns.nav.actions';
import { getReturns } from '../../../store/returns/returns.actions';
import * as NavActions from '../../../store/shipments/nav/shipments.nav.actions';
import { requestGetShipments } from '../../../store/shipments/shipments.actions';

const viewItemsSelector = createCachedSelector(
  (view) => view,
  (view) => {
    const baseView = shippingGridViews.find((c) => c.id === view.id);

    return {
      ...view,
      ...baseView,
      label: baseView.label,
    };
  },
)((view) => view.id);

const filterViewsSelector = createSelector(
  (state) => Global.selectSellerHomeCountry(state),
  (_, views) => views,
  (homeCountry, views) => {
    return views.filter((view) => {
      return !ViewFilters[view.id] || ViewFilters[view.id]({ homeCountry });
    });
  },
);
const buildTagProps = (item) => {
  return {
    to: item.navigationOverride || `${BASE_SHIPMENTS_PATH}/${item.id}`,
  };
};

const findNewNav = (navItems, viewIdFromPathname, subViewId) => {
  const viewId = subViewId ? `${viewIdFromPathname}-${subViewId}` : viewIdFromPathname;
  const navItem = navItems.reduce((found, item) => {
    const { navigationOverride: navOverride, id: navId } = item;
    if (found) {
      return found;
    }
    if (navOverride === viewId || navId === viewId) {
      return item;
    }
    return found;
  }, undefined);

  return subViewId && !navItem ? findNewNav(navItems, viewIdFromPathname) : navItem;
};

const ShippingLeftNavComponent = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { shipmentsGridViews } = useContext(ShippingLeftNavContext);
  const mappedViews = treeMap(shipmentsGridViews || emptyArray, viewItemsSelector);
  const navItems = useSelector((state) => filterViewsSelector(state, mappedViews));
  const shipmentSearchPerformed = useSelector(Global.selectShipmentsNavSearchPerformed);
  const returnSearchPerformed = useSelector(Global.selectReturnsNavSearchPerformed);
  const defaultFormData = useSelector(Global.selectShipmentsNavSearchData);

  const history = useHistory();
  const isOnReturnsAdvancedSearch = useIsOnReturnsAdvancedSearchView();
  const isOnBatchesAdvancedSearch = useIsOnBatchesAdvancedSearchView();
  const isOnFulfillmentsAdvancedSearch = useIsOnFulfillmentsAdvancedSearchView();
  const isOnAdvancedSearch =
    useIsOnShipmentsAdvancedSearchView() || isOnReturnsAdvancedSearch || isOnBatchesAdvancedSearch || isOnFulfillmentsAdvancedSearch;
  const [isAdvancedSearchExpanded, setIsAdvancedSearchExpanded] = useState(isOnAdvancedSearch);
  const currentViewId = getSelectedViewFromPathName(pathname)?.id;
  const [previousViewId, setPreviousViewId] = useState(currentViewId);
  const [previousPathName, setPreviousPathName] = useState(history.location.pathname);
  const [returnSearchInputValue, setReturnSearchInputValue] = useState('');
  const enableContainerLabels = useEnableV3ContainerLabelShipments();
  const isRefundAssistGridVisible = useIsRefundAssistGridVisible();
  const userNavItems = useMemo(() => {
    return navItems.reduce((accum, navItem) => {
      const isEodCategory = navItem.id === END_OF_DAY_CATEGORY_VIEW_ID;
      let filteredItems = navItem.items;
      if (navItem.items) {
        filteredItems = navItem.items.filter((subItem) => {
          if (isEodCategory && !enableContainerLabels && subItem.id === CONTAINER_SHIPMENTS_VIEW_ID) {
            return false;
          }
          if (!isRefundAssistGridVisible && subItem.id === REFUND_ASSIST_VIEW_ID) {
            return false;
          }
          return true;
        });
      }

      accum.push({
        ...navItem,
        items: filteredItems,
      });
      return accum;
    }, []);
  }, [navItems, enableContainerLabels, isRefundAssistGridVisible]);

  const match = useRouteMatch(`${BASE_SHIPMENTS_PATH}/:view/:subView?`);
  const returnsMatch = useRouteMatch(`${SHIPMENTS_RETURNS_PATH}`);
  const { view = '', subView = '' } = match?.params || {};
  const newView = findNewNav(shippingGridViews, view, subView);
  const selectedViewId = ShipmentsGridViewService.isValidViewId(newView?.id) ? newView.id : currentViewId;
  const newViewId = selectedViewId === REFUND_ASSIST_VIEW_ID && !isRefundAssistGridVisible ? SHIPPED_VIEW_ID : selectedViewId;

  useEffect(() => {
    if (newViewId) {
      dispatch(NavActions.shipmentsChangeView(newViewId));
      setReturnSearchInputValue();
    }
    // Clear shipment search input when navigating to returns view
    if (newViewId === RETURNS_VIEW_ID) {
      dispatch(NavActions.clearSearchInput());
    }
  }, [newViewId, dispatch]);

  // clear shipment search input when navigating away from shipments page
  useEffect(() => {
    return () => {
      dispatch(NavActions.clearSearchInput());
    };
  }, [dispatch]);

  const toggleAdvancedSearchClick = useCallback(() => {
    const isAboutToLeaveAdvancedSearch = isAdvancedSearchExpanded;
    if (isAboutToLeaveAdvancedSearch) {
      const shouldUseDefaultView =
        !previousViewId ||
        previousViewId === ADVANCED_SEARCH_VIEW_ID ||
        previousViewId === BATCHES_ADVANCED_SEARCH_VIEW_ID ||
        previousViewId === FULFILLMENTS_ADVANCED_SEARCH_VIEW_ID ||
        previousViewId === SHIPPED_VIEW_ID;
      if (shouldUseDefaultView) {
        history.push(`${BASE_SHIPMENTS_PATH}/${SHIPPED_VIEW_ID}`);
      } else {
        history.push(previousPathName);
      }
    } else {
      setPreviousViewId(currentViewId);
      setPreviousPathName(history.location.pathname);
    }
    setIsAdvancedSearchExpanded(!isAdvancedSearchExpanded);
  }, [
    setIsAdvancedSearchExpanded,
    isAdvancedSearchExpanded,
    history,
    currentViewId,
    previousViewId,
    setPreviousViewId,
    previousPathName,
    setPreviousPathName,
  ]);

  const shipmentsSearchFormProps = useMemo(
    () => ({
      clearSearchInput: () => {
        dispatch(NavActions.clearSearchInput());
      },
      searchInputValue: defaultFormData?.searchTerm,
      handleSearchInputChange: (term) => dispatch(NavActions.shippingSearchTermChange(term)),
      isHighlighted: shipmentSearchPerformed,
      onSearch: (searchData) => {
        dispatch(NavActions.shipmentsSearch(searchData));
      },
      resetGrid: () => {
        dispatch(requestGetShipments());
      },
      ariaLabelMessageDescriptor: messages.shipmentsSearchLabel,
      formTitleMessageDescriptor: messages.shipmentsSearchLabel,
      placeholderMessageDescriptor: messages.shipmentsSearchPlaceholder,
    }),
    [dispatch, defaultFormData?.searchTerm, shipmentSearchPerformed],
  );

  const returnsSearchFormProps = useMemo(
    () => ({
      clearSearchInput: () => setReturnSearchInputValue(),
      searchInputValue: returnSearchInputValue,
      handleSearchInputChange: setReturnSearchInputValue,
      isHighlighted: returnSearchPerformed,
      onSearch: (searchData) => dispatch(returnsSearch(searchData)),
      resetGrid: () => dispatch(getReturns()),
      ariaLabelMessageDescriptor: messages.returnsSearchLabel,
      formTitleMessageDescriptor: messages.returnsSearchLabel,
      placeholderMessageDescriptor: messages.returnsSearchPlaceholder,
    }),
    [dispatch, returnSearchPerformed, returnSearchInputValue],
  );
  const treeNavIsHighlighted = returnsMatch ? returnSearchPerformed : shipmentSearchPerformed;
  const isSearchAreaHighlighted = !isAdvancedSearchExpanded && (returnSearchPerformed || shipmentSearchPerformed);
  const showSearchAreaBorder = !isAdvancedSearchExpanded;

  return (
    <div className={style['tree-nav-container']}>
      <div
        className={classNames({
          [style['search-area']]: isAdvancedSearchExpanded,
          [style['search-area-highlighted']]: isSearchAreaHighlighted,
        })}
      >
        <div className={showSearchAreaBorder ? style['search-area-buttons'] : null}>
          {!isAdvancedSearchExpanded && <SearchForm {...(returnsMatch ? returnsSearchFormProps : shipmentsSearchFormProps)} />}
          <AdvancedSearchToggler isInAdvancedSearch={isAdvancedSearchExpanded} onToggleAdvancedSearch={toggleAdvancedSearchClick} />
        </div>
        {isAdvancedSearchExpanded ? <AdvancedSearch /> : null}
      </div>
      {!isAdvancedSearchExpanded && (
        <div className={style['tree-and-batches']}>
          <TreeNav
            buildTagProps={buildTagProps}
            isHighlighted={treeNavIsHighlighted}
            items={userNavItems}
            onExpandClick={noop}
            onItemSelected={noop}
          />
        </div>
      )}
    </div>
  );
};
export const ShippingLeftNav = React.memo(ShippingLeftNavComponent);
